import type { CatalogThemeOperator } from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { notify, notifyFailure } from '../feedback.ts'
import { useDebounced } from '../people/usePeople.ts'
import { platformKeys } from './keys.ts'

/**
 * The shared song catalog in the platform console (docs/15, "Song themes
 * from the shared catalog"): find a recording, read its statistics and its
 * themes with where each came from, pin or block a theme over what churches
 * agree on, and clear statistics by hand — one recording's or all of them.
 * Clearing never touches a church's songs or their themes.
 */

/** Recordings by title or artist; what is typed is asked a moment after typing stops. */
export function useAdminCatalogSearch() {
  const api = useApi()
  const [q, setQ] = useState('')
  const asked = useDebounced(q.trim(), 250)
  const list = useInfiniteQuery({
    queryKey: platformKeys.adminCatalogSearch(asked),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.admin.catalog.songs.get({
        query: {
          ...(asked ? { q: asked } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: (previous) => previous,
  })
  return {
    q,
    setQ,
    list,
    rows: list.data?.pages.flatMap((page) => page.items) ?? [],
  }
}

export type AdminCatalogRow = ReturnType<typeof useAdminCatalogSearch>['rows'][number]

/** One recording's page: statistics, themes, and what an operator may do with them. */
export function useAdminCatalogSong(id: string | null) {
  const api = useApi()
  const queryClient = useQueryClient()
  const song = useQuery({
    queryKey: platformKeys.adminCatalogSong(id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.catalog.songs({ id: id ?? '' }).get()
      if (error) throw error
      return data
    },
  })
  const settle = (data: NonNullable<typeof song.data>) => {
    queryClient.setQueryData(platformKeys.adminCatalogSong(data.id), data)
    void queryClient.invalidateQueries({ queryKey: [...platformKeys.adminCatalog(), 'search'] })
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
  }
  const setTheme = useMutation({
    mutationFn: async (input: { theme: string; operator: CatalogThemeOperator | null }) => {
      const { data, error } = await api.api.v1.admin.catalog
        .songs({ id: id ?? '' })
        .themes({ theme: input.theme })
        .put({ operator: input.operator })
      if (error) throw error
      return data
    },
    onSuccess: (data, input) => {
      settle(data)
      notify({
        tone: 'success',
        title:
          input.operator === 'pinned'
            ? 'Pinned: every church linking it wears it'
            : input.operator === 'blocked'
              ? 'Blocked: no church wears it from the catalog'
              : 'Left to the churches again',
      })
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t change the theme'),
  })
  const clear = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.admin.catalog
        .songs({ id: id ?? '' })
        ['clear-stats'].post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      settle(data)
      notify({ tone: 'success', title: 'Its statistics are cleared' })
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t clear its statistics'),
  })
  return { song, setTheme, clear }
}

export type AdminCatalogSong = NonNullable<ReturnType<typeof useAdminCatalogSong>['song']['data']>

/** "Clear all song stats": usage and theme choices across the catalog. */
export function useClearAllCatalogStats() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.admin.catalog['clear-stats'].post()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminCatalog() })
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
      notify({
        tone: 'success',
        title: 'Every song’s statistics are cleared',
        description: `${data.usageRows.toLocaleString()} weeks of usage and ${data.themeRows.toLocaleString()} theme counts. Services from tomorrow count again.`,
      })
    },
    onError: (error) => notifyFailure(error, undefined, 'Couldn’t clear the statistics'),
  })
}
